import React, { useState, useEffect } from 'react';
import './QuickBooksIntegration.css';
import { formatDateDisplay } from '../utils/timezone';
import { QUICKBOOKS_BASE_URL } from './api';

const QuickBooksIntegration = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [authUrl, setAuthUrl] = useState('');

  useEffect(() => {
    checkConnectionStatus();
    
    // Set up periodic status check every 5 minutes
    const interval = setInterval(checkConnectionStatus, 5 * 60 * 1000);
    
    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  const checkConnectionStatus = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    }
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsConnected(false);
        setCompanies([]);
        return;
      }

      const response = await fetch(`${QUICKBOOKS_BASE_URL}status/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('QuickBooks status response:', data);
        console.log('is_connected:', data.is_connected);
        console.log('companies:', data.companies);
        setIsConnected(data.is_connected);
        setCompanies(data.companies || []);
      } else {
        // If the response is not ok, assume not connected
        console.log('QuickBooks status check failed, response not ok:', response.status, response.statusText);
        setIsConnected(false);
        setCompanies([]);
      }
    } catch (err) {
      console.error('Error checking QuickBooks status:', err);
      console.log('Setting isConnected to false due to error');
      // On error, assume not connected
      setIsConnected(false);
      setCompanies([]);
    } finally {
      if (isManualRefresh) {
        setRefreshing(false);
      }
    }
  };

  const initiateConnection = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please log in first');
        return;
      }

      const response = await fetch(`${QUICKBOOKS_BASE_URL}auth-url/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        setAuthUrl(data.auth_url);
        // Redirect to QuickBooks authorization
        window.location.href = data.auth_url;
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to initiate QuickBooks connection');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error initiating connection:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCallback = async (code, realmId, state) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please log in first');
        return;
      }

      const response = await fetch(`${QUICKBOOKS_BASE_URL}callback/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: code,
          realmId: realmId,
          state: state,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsConnected(true);
        setCompanies(prev => [...prev, data.company]);
        setError(null);
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to complete QuickBooks connection');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error handling callback:', err);
    } finally {
      setLoading(false);
    }
  };

  const disconnectIntegration = async (companyId) => {
    if (!window.confirm('Are you sure you want to disconnect this QuickBooks integration?')) {
      return;
    }

    console.log('Disconnecting company with ID:', companyId);
    console.log('Company ID type:', typeof companyId);
    console.log('Company ID length:', companyId ? companyId.length : 'null/undefined');

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please log in first');
        return;
      }

      // Only include company_id if it's valid
      const requestBody = {};
      if (companyId && companyId.trim && companyId.trim().length >= 5) {
        requestBody.company_id = companyId.trim();
      } else if (companyId && companyId.length >= 5) {
        requestBody.company_id = companyId;
      }
      
      console.log('Request body:', requestBody);

      const response = await fetch(`${QUICKBOOKS_BASE_URL}disconnect/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        // Update companies list by removing the disconnected one
        const updatedCompanies = companies.filter(company => company.company_id !== companyId);
        setCompanies(updatedCompanies);
        // Update connection status based on remaining companies
        setIsConnected(updatedCompanies.length > 0);
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to disconnect QuickBooks integration');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error disconnecting:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async (companyId) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please log in first');
        return;
      }

      const response = await fetch(`${QUICKBOOKS_BASE_URL}refresh-token/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id: companyId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setError(null);
        // Optionally show success message
        alert('Token refreshed successfully');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to refresh token');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error refreshing token:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetIntegration = async () => {
    if (!window.confirm('Are you sure you want to reset the QuickBooks integration? This will disconnect all companies and clear all tokens.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please log in first');
        return;
      }

      const response = await fetch(`${QUICKBOOKS_BASE_URL}reset/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Reset response:', data);
        // Force update the UI
        setIsConnected(false);
        setCompanies([]);
        setError(null);
        alert('QuickBooks integration has been reset successfully');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to reset integration');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error resetting integration:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check for callback parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const realmId = urlParams.get('realmId');
    const state = urlParams.get('state');

    if (code && realmId) {
      handleCallback(code, realmId, state);
    }
  }, []);

  return (
    <div className="quickbooks-integration">

      {error && (
        <div className="qb-error">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {loading && (
        <div className="qb-loading">
          <p>Processing...</p>
        </div>
      )}

      <div className="qb-content">
        <div className="qb-header">
          <h2>QuickBooks Integration</h2>
          <div className="qb-header-buttons">
            <button 
              className="qb-refresh-status-btn"
              onClick={() => checkConnectionStatus(true)}
              disabled={loading || refreshing}
              title="Refresh connection status"
            >
              {refreshing ? '🔄 Refreshing...' : '🔄 Refresh Status'}
            </button>
            <button 
              className="qb-reset-btn"
              onClick={resetIntegration}
              disabled={loading || refreshing}
              title="Reset QuickBooks integration"
            >
              🔄 Reset
            </button>
          </div>
        </div>

        {!isConnected ? (
          <div className="qb-connect">
            <div className="qb-connect-info">
              <h3>Connect to QuickBooks</h3>
              <p>To get started, you'll need to authorize this application to access your QuickBooks data.</p>
              <ul>
                <li>View and manage your company information</li>
                <li>Access customer and vendor data</li>
                <li>Sync invoices and payments</li>
                <li>Generate reports</li>
              </ul>
              {refreshing && (
                <div className="qb-refresh-message">
                  <p>🔄 Checking connection status...</p>
                </div>
              )}
            </div>
            <button 
              className="qb-connect-btn"
              onClick={initiateConnection}
              disabled={loading || refreshing}
            >
              {loading ? 'Connecting...' : 'Connect to QuickBooks'}
            </button>
          </div>
        ) : (
          <div className="qb-connected">
            <div className="qb-status">
              <h3>✅ Connected to QuickBooks</h3>
              <p>You have {companies.length} QuickBooks company(ies) connected.</p>
            </div>

            <div className="qb-companies">
              {companies.map((company) => (
                <div key={company.company_id} className="qb-company-card">
                  <div className="qb-company-info">
                    <h4>{company.company_name}</h4>
                    <p>Company ID: {company.company_id}</p>
                    <p>Realm ID: {company.realm_id}</p>
                    <p>Connected: {formatDateDisplay(company.created_at)}</p>
                  </div>
                  <div className="qb-company-actions">
                    <button 
                      className="qb-refresh-btn"
                      onClick={() => refreshToken(company.company_id)}
                      disabled={loading}
                    >
                      Refresh Token
                    </button>
                    <button 
                      className="qb-disconnect-btn"
                      onClick={() => disconnectIntegration(company.company_id)}
                      disabled={loading}
                    >
                      Disconnect
                    </button>
                  </div>
                </div>
              ))}
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default QuickBooksIntegration;
